import { useEffect, useState } from 'react';
import { RiCloseCircleLine } from 'react-icons/ri';

import noAvatar from '../../assets/images/no-avatar.png';
import { accountStatusColor } from '../utils/accountStatusColor';
import { subscriptionStatus } from '../utils/subscriptionStatus';
import { Block } from './Block';
import FamilyMembersSection from './FamilyMembersSection';
import ManageSubscriptionBlock from './ManageSubscriptionBlock';
import TherapistSection from './TherapistSection';
import showUserRole from './showUserRole';

import { IUser } from '@/interfaces/entities/user';

interface ProfileProps {
	user: IUser;
	handleProfile: () => void;
}

export const Profile = ({ user, handleProfile }: ProfileProps) => {
	const [isBlockOpen, setIsBlockOpen] = useState(false);
	const [isSubBlockOpen, setIsSubBlockOpen] = useState(false);

	useEffect(() => {
		document.body.style.overflow = 'hidden';

		return () => {
			document.body.style.overflow = 'auto';
		};
	}, []);

	if (!user) {
		return null;
	}

	const openBlockModal = () => {
		setIsBlockOpen(true);
	};

	const closeBlockModal = () => {
		setIsBlockOpen(false);
	};

	const adminCantHandleSubscription =
		user.subscription &&
		!user.subscription.isPlanCancelled &&
		!user.subscription.isHardcoded;

	return (
		<div className="relative bg-white rounded-lg shadow-lg p-4 max-h-[90vh] overflow-y-auto z-20">
			<button className="absolute top-0 right-0" onClick={handleProfile}>
				<RiCloseCircleLine size={20} />
			</button>
			<h2 className="text-2xl font-bold mb-4">User Profile</h2>
			{isBlockOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
					<div className="bg-white w-1/4 p-3 rounded-xl shadow-lg border-2 border-black content-center">
						<Block user={user} handleCloseBlock={closeBlockModal} />
					</div>
				</div>
			)}
			{isSubBlockOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
					<div className="bg-white w-1/4 p-3 rounded-xl shadow-lg border-2 border-black content-center">
						<ManageSubscriptionBlock
							user={user}
							closeModal={() => setIsSubBlockOpen(false)}
						/>
					</div>
				</div>
			)}
			<div className="flex flex-row items-start">
				<div>
					<div data-test="profile-id">
						<b>ID:</b> {user.id}
					</div>
					<div data-test="profile-name">
						<b>Name:</b>{' '}
						{user.firstName || user.lastName
							? `${user.firstName} ${user.lastName}`
							: '---'}
					</div>
					<div data-test="profile-email">
						<b>Email:</b> {user.username ?? '---'}
					</div>
					<div>
						<b>Suburb:</b> {user.suburb ?? '---'}
					</div>
					<div>
						<b>Mobile:</b> {user.mobile ?? '---'}
					</div>
					<div>
						<b>Roles:</b> {showUserRole(user.roles[0]) ?? '---'}
					</div>
					<div>
						<b>Verified:</b> {user.isVerified?.toString()}
					</div>
					<div>
						<b>Account Status:</b>{' '}
						<span className={accountStatusColor(user)}>
							{user.applicationRequest}
						</span>
					</div>
					<div className="flex gap-x-4 items-center">
						<b>Subscription:</b> {subscriptionStatus(user)}
					</div>
				</div>
				<div className="flex flex-col self-start ml-12">
					{user.avatar ? (
						<img
							src={user.avatar}
							className="w-20 h-20 object-cover rounded-full"
							alt="Avatar"
						/>
					) : (
						<img
							src={noAvatar}
							className="w-20 h-20 object-cover"
							alt="Avatar"
						/>
					)}
				</div>
			</div>
			<div className="flex justify-between">
				<button
					className="mt-4 bg-blue-500 hover:bg-blue-400 text-white py-1 px-2 rounded"
					onClick={(e) => {
						e.stopPropagation();
						openBlockModal();
					}}
					data-test="manage-access-button"
				>
					MANAGE ACCESS
				</button>
				<button
					className="mt-4 bg-blue-500 hover:bg-blue-400 text-white py-1 px-2 rounded disabled:bg-gray-300"
					onClick={() => setIsSubBlockOpen(true)}
					data-test="manage-subscription-button"
					disabled={adminCantHandleSubscription}
				>
					MANAGE SUBSCRIPTION
				</button>
			</div>
			{user.family && user.family.length > 0 && (
				<FamilyMembersSection user={user} />
			)}
			{user.therapist && <TherapistSection user={user} />}
		</div>
	);
};
